'use client'

import { AlertTriangle, Check, ThumbsDown, ThumbsUp } from 'lucide-react'
import { cn } from '@/lib/utils'

export type FindingListMarker = 'dot' | 'icon' | 'thumbs'

export function FindingList({
  items,
  tone,
  max,
  marker = 'dot',
  className,
}: {
  items: string[]
  tone: 'success' | 'warning'
  max?: number
  /**
   * `dot` — dense cards.
   * `icon` — check / warning triangle.
   * `thumbs` — thumbs up / down (fit summaries).
   */
  marker?: FindingListMarker
  className?: string
}) {
  const visible = max !== undefined ? items.slice(0, max) : items
  if (visible.length === 0) return null

  const kind =
    marker === 'thumbs'
      ? tone === 'success'
        ? 'thumbUp'
        : 'thumbDown'
      : marker === 'icon'
        ? tone === 'success'
          ? 'check'
          : 'alert'
        : 'dot'

  return (
    <ul className={cn('space-y-1.5 text-sm', className)}>
      {visible.map((item) => (
        <li key={item} className="flex gap-2">
          <FindingMarker kind={kind} tone={tone} />
          <span className="min-w-0 leading-snug text-foreground/90">{item}</span>
        </li>
      ))}
    </ul>
  )
}

function FindingMarker({
  kind,
  tone,
}: {
  kind: 'dot' | 'check' | 'alert' | 'thumbUp' | 'thumbDown'
  tone: 'success' | 'warning'
}) {
  if (kind === 'check') {
    return <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" aria-hidden />
  }
  if (kind === 'alert') {
    return <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden />
  }
  if (kind === 'thumbUp') {
    return <ThumbsUp className="mt-0.5 h-3.5 w-3.5 shrink-0 text-success" aria-hidden />
  }
  if (kind === 'thumbDown') {
    return <ThumbsDown className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning" aria-hidden />
  }
  return (
    <span
      className={cn(
        'mt-[0.4rem] h-1.5 w-1.5 shrink-0 rounded-full',
        tone === 'success' ? 'bg-success' : 'bg-warning',
      )}
      aria-hidden
    />
  )
}
